import React, { useState, useEffect, useCallback } from 'react';
import { messagingAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import DirectChatDialog from './DirectChatDialog';
import { MessageSquare, Inbox, Search } from 'lucide-react';
import { Input } from '../ui/input';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

export default function MessagesTab({ classId, currentUser }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await messagingAPI.getConversations(classId);
      setConversations(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [classId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const handleOpenChat = (conv) => {
    setSelectedUser({
      id: conv.user_id,
      name: conv.user_name,
      photo_url: conv.user_photo,
      role: conv.user_role
    });
    setChatOpen(true);
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setSelectedUser(null);
    loadConversations();
  };

  const totalUnread = conversations.reduce((s, c) => s + c.unread_count, 0);
  const filtered = conversations.filter(c =>
    c.user_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="messages-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                Mensajes
              </CardTitle>
              {totalUnread > 0 && (
                <Badge className="bg-red-500 text-white border-0 text-xs">
                  {totalUnread} sin leer
                </Badge>
              )}
            </div>
            {conversations.length > 3 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar conversación..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-full sm:w-56 h-9"
                  data-testid="search-conversations"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                {conversations.length === 0 ? 'Sin conversaciones aún' : 'Sin resultados'}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {conversations.length === 0 ? 'Los mensajes con estudiantes aparecerán aquí' : 'Intenta con otro término'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((conv) => (
                <button
                  key={conv.user_id}
                  onClick={() => handleOpenChat(conv)}
                  className="w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-50 rounded-lg transition-colors text-left group"
                  data-testid={`conversation-${conv.user_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-offset-1 ring-transparent group-hover:ring-indigo-200 transition-all">
                    {conv.user_photo ? (
                      <img src={conv.user_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-semibold text-slate-600">
                        {conv.user_name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {conv.user_name}
                      </span>
                      <span className="text-[11px] text-slate-400 flex-shrink-0">
                        {timeAgo(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                        {conv.is_last_sender && <span className="text-slate-400">Tú: </span>}
                        {conv.last_message}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 w-5 h-5 bg-indigo-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid={`unread-badge-${conv.user_id}`}>
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DirectChatDialog
        open={chatOpen}
        onClose={handleCloseChat}
        classId={classId}
        otherUser={selectedUser}
        currentUser={currentUser}
      />
    </div>
  );
}
